/**
 * Core storage system types and interfaces
 */
import type { WriteGuard, WriteRefusalReason } from "../documentWriteGuard";

/**
 * Base storage options used across all storage types
 */
export interface StorageOptions {
  // Operation metadata
  operation: {
    type: "read" | "write" | "delete";
    source: string;
    timestamp: number;
  };

  // Validation requirements
  validation?: {
    schema?: unknown; // Will be defined per data type
    maxSize?: number;
    allowedTypes?: string[];
  };

  // Logging configuration
  logging?: {
    level: "none" | "basic" | "detailed";
    includeMetadata?: boolean;
  };
}

/**
 * What a `project_*` document write declares (WR-685). The coordinator hands it to
 * `IndexedDBManager.guardedSave`, where `decideDocumentWrite` rules on it inside the
 * write's own transaction.
 */
export interface DocumentWriteOptions {
  /** owner / base revision / intent — see `WriteGuard` */
  guard?: WriteGuard;
  /**
   * The recovery journal's candidate (snapshots-p2a-s10 D11; P-RING). Passed on EVERY write by
   * the saver; inert unless `recoveryJournal.armsRecovery` arms the write BY KEY inside
   * `guardedSave`. An ARMED write without it ABORTS (a code defect, never a silent unarmed
   * write). `candidate(stored)` is given the IndexedDB value at the key (read in the guarded
   * transaction) and returns the record to keep as a backup copy, or `undefined` for none
   * (nothing stored, or the stored record is positively empty). A throw ABORTS the write
   * (LD-6); the caller's own fail-safe (a throwing content predicate ⇒ content) lives INSIDE
   * `candidate`, never here.
   */
  recovery?: {
    candidate: (stored: unknown) => unknown;
  };
}

/** What the recovery journal did inside one guarded write (s10 D11). */
export interface RecoveryWriteReport {
  /** legacy slots migrated into the ring by the lazy seed */
  seeded: string[];
  /** the ring key of the new copy, if one was added */
  added?: string;
  /** ring keys removed by the oldest-past-CAP eviction (≤ 2) */
  evicted: string[];
}

/** The eager boot seed + tier-move pass's record (s10 D2b/D2c; carried in the boot result). */
export interface RecoverySeedReport {
  /** projects holding a legacy slot in either tier */
  candidates: number;
  /** ring keys written by this pass */
  seeded: string[];
  /** projects skipped because their metadata record is gone (P-685 LIVE, FR-R1) */
  skippedDead: string[];
  /** projects whose LS legacy key the proven tier move removed (CTO 65-82) */
  moved: string[];
  /** per-project failures — logged loud, never thrown */
  failed: Array<{ projectId: string; error: string }>;
  /** wall time of the whole pass */
  ms: number;
}

/**
 * Result of a storage operation
 */
export interface StorageResult<T> {
  success: boolean;
  data?: T;
  error?: StorageError;
  /**
   * A6 (general-adapter-s1): `true` only when every tier consulted GENUINELY
   * missed the key. `error.code` cannot say this — `NOT_AVAILABLE` also means
   * "tier down" / "DB failed to open". Additive: code and message are unchanged.
   */
  notFound?: boolean;
  metadata?: {
    timestamp: number;
    operation: string;
    size?: number;
    /** guarded drawing write: the revision now stored */
    rev?: number;
    /** guarded write refused — nothing was written */
    refusal?: WriteRefusalReason;
    /** guarded write: what the recovery journal did in the same transaction (s10 D11) */
    recovery?: RecoveryWriteReport;
  };
}

/**
 * Storage-specific error with additional context
 */
export class StorageError extends Error {
  constructor(
    message: string,
    public code: StorageErrorCode,
    public metadata?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "StorageError";
  }
}

/**
 * Error codes for storage operations
 */
export enum StorageErrorCode {
  // Availability errors
  NOT_AVAILABLE = "STORAGE_NOT_AVAILABLE",
  QUOTA_EXCEEDED = "STORAGE_QUOTA_EXCEEDED",

  // Operation errors
  INVALID_DATA = "INVALID_DATA",
  VALIDATION_FAILED = "VALIDATION_FAILED",
  OPERATION_FAILED = "OPERATION_FAILED",
  /** WR-685: the document write guard refused the write; nothing was written. Never retried. */
  WRITE_REFUSED = "WRITE_REFUSED",
  /** s10 D2d: a recovery slot key is deleted only through `deleteRecoveryKeys`. Never retried. */
  RECOVERY_KEY_PROTECTED = "RECOVERY_KEY_PROTECTED",
  /**
   * s10 (CTO 65-108): an armed write could not keep its safety copy for a reason OTHER than
   * storage being full, so the write did not land. Not a storage failure; never retried.
   */
  SAFETY_COPY_FAILED = "SAFETY_COPY_FAILED",

  // State errors
  VERSION_MISMATCH = "VERSION_MISMATCH",
  CORRUPTED_DATA = "CORRUPTED_DATA",

  // System errors
  INTERNAL_ERROR = "INTERNAL_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

/**
 * Base interface for all storage implementations
 */
export interface StorageLayer {
  // Core operations
  save<T>(
    key: string,
    data: T,
    options?: StorageOptions,
  ): Promise<StorageResult<void>>;
  load<T>(key: string, options?: StorageOptions): Promise<StorageResult<T>>;
  delete(key: string, options?: StorageOptions): Promise<StorageResult<void>>;

  // Storage management
  clear(options?: StorageOptions): Promise<StorageResult<void>>;
  getStatus(): Promise<StorageStatus>;
}

/**
 * Extended storage status with detailed information
 */
export interface StorageStatus {
  // Basic status
  available: boolean;
  usedSpace: number;
  totalSpace: number;

  // Detailed status
  operations?: {
    pending: number;
    completed: number;
    failed: number;
  };

  // Health metrics
  health?: {
    errorRate: number;
    avgOperationTime: number;
    lastError?: StorageError;
  };

  // Error information
  errorMessage?: string;
}

/**
 * Storage validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validation error details
 */
export interface ValidationError {
  code: string;
  message: string;
  path?: string[];
  metadata?: Record<string, unknown>;
}

/**
 * Operation log entry
 */
export interface OperationLog {
  timestamp: number;
  operation: string;
  key: string;
  status: "success" | "failure";
  duration: number;
  error?: StorageError;
  metadata?: Record<string, unknown>;
}

/**
 * Storage event for monitoring
 */
export interface StorageEvent {
  type: "operation" | "error" | "status";
  timestamp: number;
  data: unknown;
  metadata?: Record<string, unknown>;
}

/**
 * Storage event handler
 */
export type StorageEventHandler = (event: StorageEvent) => void;
